import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { G } from '@mobily/ts-belt';

import { requestGQL } from '../../../common/graphQlClient';
import { useOfflineMutation } from '../../../common/offline/useOfflineMutation';
import { isTestEnv, oneHourInMs } from '../../../config';
import { CREATE_PREMISE_INSTALLATION_BOOKING, GET_PREMISE, GetPremiseResponse, UPDATE_PREMISE_NOTES } from '../api';
import { parseGetPremiseResponse, PremiseOnSinglePremise } from '../Premise';

export const premiseByIdKey = (id: number | null) => ['premiseById', id] as const;
export const fetchPremiseById = (id: number | null) => () => requestGQL<GetPremiseResponse>(GET_PREMISE, { id });

/** Payload shape for updatePremiseNotes mutation - matches GraphQL variables */
type UpdatePremiseNotesPayload = {
  id: number;
  premisesPatch: Partial<PremiseOnSinglePremise>;
};

/** Payload shape for createPremiseInstallationBooking mutation - matches GraphQL variables */
type CreatePremiseInstallationBookingPayload = {
  premiseId: number;
} & Partial<PremiseOnSinglePremise>;

type UsePremiseOptions = {
  /** Property ID for offline mutation grouping */
  propertyId?: number | null;
  /** Callback when mutation is queued for offline sync */
  onOfflineQueued?: () => void;
};

export const usePremise = (id: number | null, options: UsePremiseOptions = {}) => {
  const { propertyId = null, onOfflineQueued } = options;

  // TODO: extract that usequery so these options won't be necessary
  const {
    data,
    isLoading: isLoadingPremise,
    isError: isErrorPremise,
  } = useQuery(premiseByIdKey(id), fetchPremiseById(id), {
    enabled: G.isNotNullable(id),
    refetchOnWindowFocus: false,
    retry: isTestEnv ? 0 : 3,
    staleTime: oneHourInMs,
  });

  const updateNotesMutation = useOfflineMutation<unknown, unknown, UpdatePremiseNotesPayload>({
    mutationFn: (payload) => requestGQL(UPDATE_PREMISE_NOTES, payload),
    mutationName: 'updatePremiseNotes',
    graphqlDocument: UPDATE_PREMISE_NOTES,
    entity: 'premise',
    operationType: 'UPDATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [premiseByIdKey(id)],
    onOfflineQueued,
  });

  const createBookingMutation = useOfflineMutation<unknown, unknown, CreatePremiseInstallationBookingPayload>({
    mutationFn: (payload) => requestGQL(CREATE_PREMISE_INSTALLATION_BOOKING, payload),
    mutationName: 'createPremiseInstallationBooking',
    graphqlDocument: CREATE_PREMISE_INSTALLATION_BOOKING,
    entity: 'premiseInstallationBooking',
    operationType: 'CREATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [premiseByIdKey(id)],
    onOfflineQueued,
  });

  const parsedPremise = useMemo(() => {
    if (G.isNullable(data)) {
      return null;
    }

    return parseGetPremiseResponse(data);
  }, [data]);

  // Wrapper function that includes the premise ID in the payload
  const updatePremiseNotes = (premisesPatch: Partial<PremiseOnSinglePremise>) => {
    if (id === null) {
      // eslint-disable-next-line no-console
      console.error('[usePremise] Cannot update premise notes: id is null');
      return;
    }
    updateNotesMutation.mutate({ id, premisesPatch });
  };

  // Wrapper function that includes the premise ID in the payload
  const createPremiseInstallationBooking = (bookingData: Partial<PremiseOnSinglePremise>) => {
    if (id === null) {
      // eslint-disable-next-line no-console
      console.error('[usePremise] Cannot create booking: id is null');
      return;
    }
    createBookingMutation.mutate({ premiseId: id, ...bookingData });
  };

  return {
    premise: parsedPremise,
    isLoadingPremise,
    isErrorPremise,
    updatePremiseNotes,
    isLoadingUpdatePremiseNotes: updateNotesMutation.isLoading,
    isErrorUpdatePremiseNotes: updateNotesMutation.isError,
    isSuccessUpdatePremiseNotes: updateNotesMutation.isSuccess,
    createPremiseInstallationBooking,
    isLoadingPremiseInstallationBooking: createBookingMutation.isLoading,
    isErrorPremiseInstallationBooking: createBookingMutation.isError,
    isSuccessPremiseInstallationBooking: createBookingMutation.isSuccess,
    isOnline: updateNotesMutation.isOnline,
    pendingCount: updateNotesMutation.pendingCount,
  };
};
